import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import moment, { type Moment } from 'moment';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';

import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import useCompanyDateFormat from 'hooks/useCompanyDateFormat';
import { useBankFeedRouteContext } from 'pages/company/BankFeedRoute/context';
import { computeEmptyVariant } from 'pages/company/BankFeedRoute/components/EmptyStates/computeEmptyVariant';
import type { DateRangePreset } from 'pages/company/BankFeedRoute/components/FilterToolbar/types';
import {
  DATE_RANGE_PRESETS,
  customRangeToMomentPair,
  dateRangeToSelectValue,
  isDateRangeCustom,
  type DateRangeSelectValue,
} from 'pages/company/BankFeedRoute/components/FilterToolbar/dateRange';

// Grow-to-content between a sensible floor and cap: presets keep the familiar ~180px,
// the "Custom" label expands the Select up to the cap, beyond which MUI's
// `.MuiSelect-select` truncates with an ellipsis.
const DATE_RANGE_SELECT_MIN_WIDTH = 180;
const DATE_RANGE_SELECT_MAX_WIDTH = 280;
// Each From/To picker input — wide enough for a fully-formatted date (e.g. "DD/MM/YYYY")
// plus the calendar adornment without truncating the year.
const DATE_INPUT_WIDTH = 170;

// Bank-tab only: the Credit Card tab uses CardDateRangeFilter (DA-19485), so this preset
// range filter renders solely on the Bank (CHECKING) tab and reads the "Value date range" label.
const DATE_RANGE_PRESET_LABEL_KEYS: Record<DateRangePreset, string> = {
  last_30_days: 'company.bankFeed.transactions.toolbar.dateRange.last30',
  last_90_days: 'company.bankFeed.transactions.toolbar.dateRange.last90',
  last_12_months: 'company.bankFeed.transactions.toolbar.dateRange.last12Months',
};

const DATE_RANGE_PRESET_DEFAULT_MESSAGES: Record<DateRangePreset, string> = {
  last_30_days: 'Last 30 days',
  last_90_days: 'Last 90 days',
  last_12_months: 'Last 12 months',
};

// Date-range toolbar filter: a preset Select plus, in "Custom" mode, two single From/To
// date inputs. The applied `toolbarFilters.dateRange` only flips to a custom window once
// BOTH bounds are valid (`commitCustomRange`) — a half-filled range never triggers a fetch.
// Cross-field bounds (To `disabled` until From is set; `minDate`/`maxDate` mirroring) keep
// users from constructing an invalid `from > to` window in the first place.
const DateRangeFilter: React.FC = () => {
  const intl = useIntl();
  const { dateFormat } = useCompanyDateFormat();

  const { toolbarFilters, setToolbarFilters, accountType, summary } = useBankFeedRouteContext();
  const { dateRange } = toolbarFilters;

  // Pre-data empty state: blank + disabled (mirrors the Currency / Search controls). The
  // underlying `dateRange` value is left untouched — only its display is suppressed.
  const isEmptyView = computeEmptyVariant(summary, accountType) !== null;

  const initialCustom = isDateRangeCustom(dateRange);
  const [customMode, setCustomMode] = useState(initialCustom);
  const [pendingFrom, setPendingFrom] = useState<Moment | null>(
    initialCustom ? customRangeToMomentPair(dateRange)[0] : null,
  );
  const [pendingTo, setPendingTo] = useState<Moment | null>(
    initialCustom ? customRangeToMomentPair(dateRange)[1] : null,
  );

  // Lower bound shared by BOTH the picker props and the commit validator below, so the
  // red-error state the user sees and the range we actually apply can never diverge. 1900-01-01
  // mirrors MUI X's own default `minDate` and guards against absurd typed years (e.g. "0011"
  // from a half-typed 4-digit year).
  const minSelectableDate = useMemo(() => moment('1900-01-01', 'YYYY-MM-DD', true), []);
  // Upper bound mirrors MUI X's own default `maxDate` (2099-12-31), same as the 1900 floor mirrors
  // its default `minDate` — so the red-error state a user sees and the range we actually apply stay
  // aligned at BOTH ends. Future dates within this window are allowed (DA-19397).
  const maxSelectableDate = useMemo(() => moment('2099-12-31', 'YYYY-MM-DD', true), []);

  // Single bounds check reused for `from` and `to`: a parseable date within [floor, ceiling].
  // Future dates are allowed up to the ceiling (DA-19397 — value dates can be in the future).
  // `from <= to` is checked once in `commitCustomRange`.
  const isWithinBounds = useCallback(
    (date: Moment) =>
      date.isValid() &&
      date.isSameOrAfter(minSelectableDate, 'day') &&
      date.isSameOrBefore(maxSelectableDate, 'day'),
    [minSelectableDate, maxSelectableDate],
  );

  // Keep custom mode in sync with the applied `dateRange` when it changes externally — restored
  // on navigation, a future Reset-filters action, or the per-tab reset on tab switch (the parent
  // resets `toolbarFilters` while this toolbar stays mounted). Without the `else`, an external
  // reset to a preset would leave the Select stuck on `Custom` with From/To visible while the
  // applied filter is actually a preset. Picking `Custom` from the Select does NOT trip this
  // effect — it leaves `dateRange` on the preset until both bounds are valid — so the inputs are
  // not hidden mid-edit.
  useEffect(() => {
    if (isDateRangeCustom(dateRange)) {
      const [from, to] = customRangeToMomentPair(dateRange);
      setPendingFrom(from);
      setPendingTo(to);
      setCustomMode(true);
    } else {
      setCustomMode(false);
    }
  }, [dateRange]);

  // Commit only a complete, in-bounds, ordered range — otherwise leave the active filter
  // untouched so a half-filled or invalid range never fetches or produces a `from > to` window.
  //
  // Validity is DERIVED here (a pure check over the two pending values), not tracked in per-field
  // error state. In @mui/x-date-pickers v6.19.3 there is no public validation API to lean on:
  // `onChange`'s `context.validationError` is strictly per-field and, with two single pickers,
  // cannot see cross-field bounds (editing To fires onChange with `validationError == null` even
  // when From is out of range — see mui/mui-x#6421). A public `useValidation` / `validateDate`
  // only arrives in v7.18+ (object form in v8).
  //
  // TODO(@mui/x-date-pickers upgrade): once on v7.18+/v8, replace this hand-rolled check with the
  // public validator — e.g. derive each field's error via `useValidation`/`validateDate` against
  // the same `minDate`/`maxDate` props, or move to a Pro range component whose single `onChange`
  // carries a range-aware `validationError` tuple. Then `minSelectableDate` and `isWithinBounds`
  // can go away.
  const commitCustomRange = useCallback(
    (from: Moment | null, to: Moment | null) => {
      if (from && to && isWithinBounds(from) && isWithinBounds(to) && !from.isAfter(to, 'day')) {
        // Return `prev` when the applied custom range already covers the same days — the grid
        // refresh effect keys on `toolbarFilters` identity, so a fresh object with identical
        // bounds would trigger a redundant purge + first-page jump + refetch. Mirrors the
        // Search field's `prev.search === next ? prev : …` guard.
        setToolbarFilters((prev) => {
          const sameDays =
            isDateRangeCustom(prev.dateRange) &&
            moment(prev.dateRange.from).isSame(from, 'day') &&
            moment(prev.dateRange.to).isSame(to, 'day');
          return sameDays
            ? prev
            : { ...prev, dateRange: { from: from.toDate(), to: to.toDate() } };
        });
      }
    },
    [isWithinBounds, setToolbarFilters],
  );

  const handleFromChange = useCallback(
    (value: Moment | null) => {
      setPendingFrom(value);
      commitCustomRange(value, pendingTo);
    },
    [commitCustomRange, pendingTo],
  );

  const handleToChange = useCallback(
    (value: Moment | null) => {
      setPendingTo(value);
      commitCustomRange(pendingFrom, value);
    },
    [commitCustomRange, pendingFrom],
  );

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<DateRangeSelectValue>) => {
      const next = event.target.value as DateRangeSelectValue;
      if (next === 'custom') {
        // Seed the inputs from the applied range: prefilled if a custom window is already
        // active, empty if a preset is applied — so the shown dates never disagree with the
        // applied filter (no stale prefill from an earlier custom range).
        const [from, to] = isDateRangeCustom(dateRange)
          ? customRangeToMomentPair(dateRange)
          : [null, null];
        setPendingFrom(from);
        setPendingTo(to);
        setCustomMode(true);
        return;
      }
      setCustomMode(false);
      setToolbarFilters((prev) => ({ ...prev, dateRange: next }));
    },
    [dateRange, setToolbarFilters],
  );

  const selectValue: DateRangeSelectValue = customMode
    ? 'custom'
    : dateRangeToSelectValue(dateRange);

  // Custom shows its literal label — the picked dates live in the From/To inputs, not the Select.
  const renderValue = useCallback(
    (value: DateRangeSelectValue): React.ReactNode => {
      if (value === 'custom') {
        return intl.formatMessage({
          id: 'company.bankFeed.transactions.toolbar.dateRange.custom',
          defaultMessage: 'Custom',
        });
      }
      return intl.formatMessage({
        id: DATE_RANGE_PRESET_LABEL_KEYS[value],
        defaultMessage: DATE_RANGE_PRESET_DEFAULT_MESSAGES[value],
      });
    },
    [intl],
  );

  const label = intl.formatMessage({
    id: 'company.bankFeed.transactions.toolbar.dateRange.labelBank',
    defaultMessage: 'Value date range',
  });
  const fromLabel = intl.formatMessage({ id: 'dayPicker.from', defaultMessage: 'From' });
  const toLabel = intl.formatMessage({ id: 'dayPicker.to', defaultMessage: 'To' });

  return (
    <>
      <FormControl
        size="small"
        sx={{
          minWidth: DATE_RANGE_SELECT_MIN_WIDTH,
          maxWidth: DATE_RANGE_SELECT_MAX_WIDTH,
          backgroundColor: 'common.white',
        }}
        disabled={isEmptyView}
      >
        <InputLabel id="bank-feed-date-range-select-label">{label}</InputLabel>
        {isEmptyView ? (
          // Pre-data empty state: blank, disabled placeholder. value="" + an empty MenuItem keeps
          // the value valid (no MUI out-of-range warning) and the field unfilled, so the label
          // rests in place instead of floating up as if a date range were selected.
          <Select labelId="bank-feed-date-range-select-label" value="" label={label}>
            <MenuItem value="" />
          </Select>
        ) : (
          <Select<DateRangeSelectValue>
            labelId="bank-feed-date-range-select-label"
            value={selectValue}
            label={label}
            onChange={handleSelectChange}
            renderValue={renderValue}
          >
            {DATE_RANGE_PRESETS.map((preset) => (
              <MenuItem key={preset} value={preset}>
                <FormattedMessage
                  id={DATE_RANGE_PRESET_LABEL_KEYS[preset]}
                  defaultMessage={DATE_RANGE_PRESET_DEFAULT_MESSAGES[preset]}
                />
              </MenuItem>
            ))}
            <MenuItem value="custom">
              <FormattedMessage
                id="company.bankFeed.transactions.toolbar.dateRange.custom"
                defaultMessage="Custom"
              />
            </MenuItem>
          </Select>
        )}
      </FormControl>

      {customMode && !isEmptyView && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DesktopDatePicker
            label={fromLabel}
            format={dateFormat}
            value={pendingFrom}
            onChange={handleFromChange}
            minDate={minSelectableDate}
            // Upper-bound From by To only when To is itself in range — otherwise an out-of-range
            // To (e.g. a typed year 0011) would push From's `maxDate` below a valid From and flag
            // it red for the wrong reason. Same `isWithinBounds` source as the commit gate.
            maxDate={pendingTo && isWithinBounds(pendingTo) ? pendingTo : maxSelectableDate}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: DATE_INPUT_WIDTH, backgroundColor: 'common.white' },
              },
            }}
          />
          <Typography component="span" color="text.secondary" aria-hidden>
            –
          </Typography>
          <DesktopDatePicker
            label={toLabel}
            format={dateFormat}
            value={pendingTo}
            onChange={handleToChange}
            disabled={pendingFrom == null}
            // Lower-bound To by From — but only when From is itself in range; clamping to the
            // floor otherwise keeps To red (rather than validating it against an out-of-range
            // From). The commit gate is the real backstop; this is just consistent UX feedback.
            minDate={pendingFrom && isWithinBounds(pendingFrom) ? pendingFrom : minSelectableDate}
            maxDate={maxSelectableDate}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: DATE_INPUT_WIDTH, backgroundColor: 'common.white' },
              },
            }}
          />
        </Box>
      )}
    </>
  );
};

export default DateRangeFilter;
